import React, { useState, useEffect } from "react";
import { useObserver } from "mobx-react";
import { useStores } from "../../store";
import {
  FaTimes,
  FaPlus,
  FaTrash,
  FaCheck,
  FaFileUpload,
  FaDownload,
  FaEye,
} from "react-icons/fa";
import { uploadFileToAzure } from "../../services/azure";

const VerificationPopup = ({
  isOpen,
  onClose,
  applicationId,
  userId,
  verificationData = null,
  isEdit = false,
  onSuccess,
}) => {
  const { RecuiterStore } = useStores();
  const [formData, setFormData] = useState({
    verifications: [
      {
        verificationType: "education",
        name: "",
        description: "",
        verifiedBy: {
          name: "",
          email: "",
          phone: "",
          role: "",
          description: "",
        },
        status: 0,
        priority: "medium",
        proof: [],
        notes: [],
        clientDetails: "",
        otherDetails: "",
      },
    ],
    globalNotes: [],
  });

  const [currentVerificationIndex, setCurrentVerificationIndex] = useState(0);
  const [showAddVerification, setShowAddVerification] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});

  const verificationTypes = [
    "education",
    "experience",
    "criminal_check",
    "identity",
    "address",
    "employment",
    "reference",
    "certification",
    "license",
    "other",
  ];

  const statusOptions = [
    { value: 0, label: "Pending", color: "bg-yellow-100 text-yellow-800" },
    { value: 1, label: "Verified", color: "bg-green-100 text-green-800" },
    { value: 2, label: "Unverified", color: "bg-gray-100 text-gray-800" },
    { value: 3, label: "Rejected", color: "bg-red-100 text-red-800" },
    { value: 4, label: "Under Review", color: "bg-blue-100 text-blue-800" },
  ];

  const priorityOptions = [
    { value: "low", label: "Low", color: "bg-gray-100 text-gray-800" },
    { value: "medium", label: "Medium", color: "bg-yellow-100 text-yellow-800" },
    { value: "high", label: "High", color: "bg-orange-100 text-orange-800" },
    { value: "urgent", label: "Urgent", color: "bg-red-100 text-red-800" },
  ];

  const proofTypes = [
    "image",
    "video",
    "audio",
    "document",
    "pdf",
    "docx",
    "link",
    "csv",
    "xlsx",
    "pptx",
    "zip",
    "text",
    "other",
  ];

  useEffect(() => {
    if (isOpen) {
      if (isEdit && verificationData) {
        // When editing, populate form with the specific verification data
        setFormData({
          verifications: [
            {
              verificationType: verificationData.verificationType || "education",
              name: verificationData.name || "",
              description: verificationData.description || "",
              verifiedBy: {
                name: verificationData.verifiedBy?.name || "",
                email: verificationData.verifiedBy?.email || "",
                phone: verificationData.verifiedBy?.phone || "",
                role: verificationData.verifiedBy?.role || "",
                description: verificationData.verifiedBy?.description || "",
              },
              status: verificationData.status || 0,
              priority: verificationData.priority || "medium",
              proof: verificationData.proof || [],
              notes: verificationData.notes || [],
              clientDetails: verificationData.clientDetails || "",
              otherDetails: verificationData.otherDetails || "",
            },
          ],
          globalNotes: verificationData.globalNotes || [],
        });
      } else {
        setFormData({
          verifications: [
            {
              verificationType: "education",
              name: "",
              description: "",
              verifiedBy: {
                name: "",
                email: "",
                phone: "",
                role: "",
                description: "",
              },
              status: 0,
              priority: "medium",
              proof: [],
              notes: [],
              clientDetails: "",
              otherDetails: "",
            },
          ],
          globalNotes: [],
        });
      }
    }
  }, [isOpen, isEdit, verificationData]);

  const handleInputChange = (field, value, verificationIndex = null) => {
    if (verificationIndex !== null) {
      setFormData((prev) => ({
        ...prev,
        verifications: prev.verifications.map((verification, index) =>
          index === verificationIndex
            ? { ...verification, [field]: value }
            : verification
        ),
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    }
  };

  const handleVerifiedByChange = (field, value, verificationIndex) => {
    setFormData((prev) => ({
      ...prev,
      verifications: prev.verifications.map((verification, index) =>
        index === verificationIndex
          ? {
              ...verification,
              verifiedBy: { ...verification.verifiedBy, [field]: value },
            }
          : verification
      ),
    }));
  };

  const handleFileUpload = async (files, verificationIndex) => {
    if (!files || files.length === 0) return;

    setUploadingFiles(true);
    const uploadedProofs = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const fileId = `${Date.now()}_${i}`;
      
      try {
        setUploadProgress(prev => ({ ...prev, [fileId]: 0 }));
        
        const result = await uploadFileToAzure(
          `${verificationIndex}_${file.name}`,
          file,
          { action: "resume" },
          (progress) => {
            setUploadProgress(prev => ({ ...prev, [fileId]: progress }));
          }
        );

        if (result.success) {
          // Map file extensions to valid proof types
          const getProofType = (fileType) => {
            const extension = file.name.split('.').pop().toLowerCase();
            switch (extension) {
              case 'pdf': return 'pdf';
              case 'doc':
              case 'docx': return 'docx';
              case 'jpg':
              case 'jpeg':
              case 'png':
              case 'gif': return 'image';
              case 'mp4':
              case 'avi':
              case 'mov': return 'video';
              case 'mp3':
              case 'wav':
              case 'm4a': return 'audio';
              case 'txt': return 'text';
              case 'csv': return 'csv';
              case 'xlsx':
              case 'xls': return 'xlsx';
              case 'pptx':
              case 'ppt': return 'pptx';
              case 'zip':
              case 'rar': return 'zip';
              default: return 'other';
            }
          };

          uploadedProofs.push({
            type: getProofType(file.type),
            name: file.name,
            description: `Uploaded proof document: ${file.name}`,
            url: result.url,
            additionalInfo: `File size: ${(file.size / 1024 / 1024).toFixed(2)} MB`,
            uploadedAt: Date.now(), // Use timestamp as number
            fileSize: file.size
          });
        } else {
          console.error('Upload failed for file:', file.name, result.error);
        }
      } catch (error) {
        console.error('Error uploading file:', file.name, error);
      } finally {
        setUploadProgress(prev => {
          const newProgress = { ...prev };
          delete newProgress[fileId];
          return newProgress;
        });
      }
    }

    // Add uploaded proofs to the verification
    if (uploadedProofs.length > 0) {
      setFormData((prev) => ({
        ...prev,
        verifications: prev.verifications.map((verification, index) =>
          index === verificationIndex
            ? {
                ...verification,
                proof: [...(verification.proof || []), ...uploadedProofs],
              }
            : verification
        ),
      }));
    }

    setUploadingFiles(false);
  };

  const removeProof = (verificationIndex, proofIndex) => {
    setFormData((prev) => ({
      ...prev,
      verifications: prev.verifications.map((verification, index) =>
        index === verificationIndex
          ? {
              ...verification,
              proof: verification.proof.filter((_, i) => i !== proofIndex),
            }
          : verification
      ),
    }));
  };

  const handleSubmit = async () => {
    try {
      let result;
      if (isEdit && verificationData) {
        // For editing, we need to use the parent verification document ID
        // and send the complete verification document data
        const parentVerificationId = verificationData.parentVerificationId || verificationData._id || verificationData.id;
        
        console.log("Editing verification with data:", {
          verificationData,
          parentVerificationId,
          formData: formData.verifications[0]
        });
        
        if (!parentVerificationId) {
          alert("Verification ID not found. Cannot update verification.");
          return;
        }

        // Get the current verification document data from the parent
        const currentVerificationData = await RecuiterStore.getVerificationByApplicationAndUser(
          applicationId,
          userId
        );

        if (!currentVerificationData.success) {
          alert("Failed to fetch current verification data.");
          return;
        }

        // Update the specific verification in the verifications array
        // Use createdAt as unique identifier to distinguish between verifications of same type
        const updatedVerifications = currentVerificationData.data.verifications.map(verification => {
          const isTargetVerification = verification.verificationType === verificationData.verificationType && 
              verification.createdAt === verificationData.createdAt;
          
          console.log("Checking verification:", {
            verificationType: verification.verificationType,
            createdAt: verification.createdAt,
            targetVerificationType: verificationData.verificationType,
            targetCreatedAt: verificationData.createdAt,
            isTargetVerification
          });
          
          if (isTargetVerification) {
            console.log("Updating verification:", verification);
            return {
              ...verification,
              ...formData.verifications[0],
              // Preserve the original createdAt timestamp
              createdAt: verification.createdAt
            };
          }
          return verification;
        });

        // Prepare the complete document data for update
        const completeDocumentData = {
          overallStatus: currentVerificationData.data.overallStatus,
          verifications: updatedVerifications,
          globalNotes: currentVerificationData.data.globalNotes || []
        };

        console.log("Sending complete document data:", completeDocumentData);

        // Use the updateVerification method to update the entire document
        result = await RecuiterStore.updateVerification(
          parentVerificationId,
          completeDocumentData
        );
      } else {
        // For creating new verification, first check if a verification document already exists
        const existingVerificationData = await RecuiterStore.getVerificationByApplicationAndUser(
          applicationId,
          userId
        );

        if (existingVerificationData.success && existingVerificationData.data) {
          // If verification document exists, add the new verification to it
          console.log("Adding verification to existing document:", existingVerificationData.data);
          
          const newVerification = {
            ...formData.verifications[0],
            createdAt: Date.now() // Add timestamp for new verification
          };

          const updatedVerifications = [
            ...existingVerificationData.data.verifications,
            newVerification
          ];

          const completeDocumentData = {
            overallStatus: existingVerificationData.data.overallStatus,
            verifications: updatedVerifications,
            globalNotes: existingVerificationData.data.globalNotes || []
          };

          console.log("Adding verification to existing document:", completeDocumentData);

          result = await RecuiterStore.updateVerification(
            existingVerificationData.data._id,
            completeDocumentData
          );
        } else {
          // If no verification document exists, create a new one
          console.log("Creating new verification document");
          result = await RecuiterStore.createVerification(
            applicationId,
            userId,
            formData
          );
        }
      }

      if (result.success) {
        onSuccess && onSuccess(result.data);
        onClose();
      } else {
        alert(result.message || "Failed to save verification");
      }
    } catch (error) {
      console.error("Error saving verification:", error);
      alert("An error occurred while saving verification");
    }
  };

  return useObserver(() => {
    if (!isOpen) return null;

    return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-2xl font-bold text-gray-900">
            {isEdit ? "Edit Verification" : "Add Verification"}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <FaTimes className="text-xl" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          {/* Basic Information */}
          <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Verification Details
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Verification Type *
                </label>
                <select
                  value={formData.verifications[0]?.verificationType || ""}
                  onChange={(e) =>
                    handleInputChange("verificationType", e.target.value, 0)
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  {verificationTypes.map((type) => (
                    <option key={type} value={type}>
                      {type.replace("_", " ").toUpperCase()}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name *
                </label>
                <input
                  type="text"
                  value={formData.verifications[0]?.name || ""}
                  onChange={(e) =>
                    handleInputChange("name", e.target.value, 0)
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Enter verification name"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description *
                </label>
                <textarea
                  value={formData.verifications[0]?.description || ""}
                  onChange={(e) =>
                    handleInputChange("description", e.target.value, 0)
                  }
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Enter verification description"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  value={formData.verifications[0]?.status || 0}
                  onChange={(e) =>
                    handleInputChange("status", parseInt(e.target.value), 0)
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  {statusOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Priority
                </label>
                <select
                  value={formData.verifications[0]?.priority || "medium"}
                  onChange={(e) =>
                    handleInputChange("priority", e.target.value, 0)
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  {priorityOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div> */}
            </div>
          </div>

          {/* Verifier Information */}
          <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Verifier Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Verifier Name *
                </label>
                <input
                  type="text"
                  value={formData.verifications[0]?.verifiedBy?.name || ""}
                  onChange={(e) =>
                    handleVerifiedByChange("name", e.target.value, 0)
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Enter verifier name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={formData.verifications[0]?.verifiedBy?.email || ""}
                  onChange={(e) =>
                    handleVerifiedByChange("email", e.target.value, 0)
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Enter verifier email"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone
                </label>
                <input
                  type="tel"
                  value={formData.verifications[0]?.verifiedBy?.phone || ""}
                  onChange={(e) =>
                    handleVerifiedByChange("phone", e.target.value, 0)
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Enter verifier phone"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Role
                </label>
                <input
                  type="text"
                  value={formData.verifications[0]?.verifiedBy?.role || ""}
                  onChange={(e) =>
                    handleVerifiedByChange("role", e.target.value, 0)
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Enter verifier role"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={formData.verifications[0]?.verifiedBy?.description || ""}
                  onChange={(e) =>
                    handleVerifiedByChange("description", e.target.value, 0)
                  }
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Enter verifier description"
                />
              </div>
            </div>
          </div>

          {/* Additional Details */}
          {/* <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Additional Details
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Client Details
                </label>
                <textarea
                  value={formData.verifications[0]?.clientDetails || ""}
                  onChange={(e) =>
                    handleInputChange("clientDetails", e.target.value, 0)
                  }
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Enter client-specific details"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Other Details
                </label>
                <textarea
                  value={formData.verifications[0]?.otherDetails || ""}
                  onChange={(e) =>
                    handleInputChange("otherDetails", e.target.value, 0)
                  }
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Enter other relevant details"
                />
              </div>
            </div>
          </div> */}

          {/* Proof Documents Section */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Proof Documents
            </h3>
            
            {/* File Upload Area */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Upload Proof Documents
              </label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-primary transition-colors">
                <input
                  type="file"
                  multiple
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.txt,.csv,.xlsx,.pptx,.zip"
                  onChange={(e) => handleFileUpload(e.target.files, 0)}
                  className="hidden"
                  id="file-upload"
                  disabled={uploadingFiles}
                />
                <label
                  htmlFor="file-upload"
                  className={`cursor-pointer flex flex-col items-center ${
                    uploadingFiles ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                >
                  <FaFileUpload className="text-4xl text-gray-400 mb-2" />
                  <p className="text-sm text-gray-600 mb-1">
                    {uploadingFiles ? "Uploading files..." : "Click to upload or drag and drop"}
                  </p>
                  <p className="text-xs text-gray-500">
                    PDF, DOC, DOCX, JPG, PNG, TXT, CSV, XLSX, PPTX, ZIP (Max 10MB each)
                  </p>
                </label>
              </div>
              
              {/* Upload Progress */}
              {Object.keys(uploadProgress).length > 0 && (
                <div className="mt-4 space-y-2">
                  {Object.entries(uploadProgress).map(([fileId, progress]) => (
                    <div key={fileId} className="bg-gray-100 rounded-lg p-2">
                      <div className="flex items-center justify-between text-sm">
                        <span>Uploading file...</span>
                        <span>{progress}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                        <div
                          className="bg-primary h-2 rounded-full transition-all duration-300"
                          style={{ width: `${progress}%` }}
                        ></div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Uploaded Files List */}
            {formData.verifications[0]?.proof && formData.verifications[0].proof.length > 0 && (
              <div className="space-y-3">
                <h4 className="text-sm font-medium text-gray-700">
                  Uploaded Documents ({formData.verifications[0].proof.length})
                </h4>
                <div className="space-y-2">
                  {formData.verifications[0].proof.map((proof, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between bg-gray-50 rounded-lg p-3 border"
                    >
                      <div className="flex items-center gap-3 flex-1">
                        <div className="flex-shrink-0">
                          <FaFileUpload className="text-primary text-lg" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {proof.name}
                          </p>
                          <p className="text-xs text-gray-500">
                            {proof.type.toUpperCase()} • {proof.additionalInfo}
                          </p>
                          {proof.uploadedAt && (
                            <p className="text-xs text-gray-400">
                              Uploaded: {new Date(proof.uploadedAt).toLocaleString()}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => window.open(proof.url, '_blank')}
                          className="p-1 text-gray-400 hover:text-primary transition-colors"
                          title="View/Download"
                        >
                          <FaEye className="text-sm" />
                        </button>
            <button
                          onClick={() => removeProof(0, index)}
                          className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                          title="Remove"
                        >
                          <FaTrash className="text-sm" />
            </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={RecuiterStore.verificationLoader || uploadingFiles}
            className="px-6 py-2 bg-primary text-white rounded-md hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {RecuiterStore.verificationLoader ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Saving...
              </>
            ) : uploadingFiles ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Uploading...
              </>
            ) : (
              <>
                <FaCheck className="text-sm" />
                {isEdit ? "Update" : "Create"} Verification
              </>
            )}
          </button>
        </div>
      </div>
    </div>
    );
  });
};

export default VerificationPopup;